import { Button } from '@clay-run/glaze/button';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryState } from 'nuqs';
import { flowUrlParams } from '../../../../../../mini-apps/_shared/url-params';
import { toast } from 'sonner';
import {
  CONTACT_PRESETS,
  CONTACT_PRESET_ID_ALIASES,
  CONTACT_ACCOUNT_DEFAULT_SORT,
  CONTACT_SIGNAL_FIELD_KEYS,
  CONTACT_PLAY_FIELD_KEYS,
  CONTACT_PLAY_CANDIDATES_OFF_MOUNT_SORT,
  type ContactRow,
} from '@shared/flow/contact-view';
import type { FlowView } from '@shared/flow/view';
import type { FilterGroup } from '@shared/flow/filter';
import { sanitizePlayScopeFilters } from '@shared/flow/plays-view';
import { playSectionPinnedConditions, type PlayPeopleSection } from '@shared/flow/play-people';
import { countSignalConditionGroups, requestNarrowsSignals } from '@shared/flow/signal-narrowing';
import { applySignalScopeToFilters, outerOrBlocksSignalScope } from '@shared/flow/signal-scope-apply';
import { CONTACT_FILTER_FIELDS } from '@shared/flow/filter-fields';
import type { FlowTableConfig, FlowTableProps, FlowTableSelectionConfig } from '../types';
import { ContactDetail } from './ContactDetailPanels';
import { WorkflowsDropdown } from './ContactActionMenus';
import { useDebouncedValue } from '../useDebouncedValue';
import { useFlowIdentity } from '../useFlowIdentity';
import { withRoleDefaults } from '../roleDefaults';
import { buildMeFilterEmptyState } from '../meEmptyState';
import { useViewStateMachine, type ViewKind } from '../views/useViewStateMachine';
import { splitHiddenGroupBy } from '../table-core/grouping';
import { useFlowViewState, useFlowViewStatePersist } from '../views/useViewState';
import { withoutLegacyPlayCandidatesWorkingState } from './playCandidatesLegacyDefault';
import {
  usePlaySectionSessionSeed,
  useRememberPlaySectionSessionView,
  withPlaySectionSessionWorkingState,
} from './playSectionSessionViews';
import { CROSS_APP_STATE_KEY } from '../views/syntheticColumns';
import { isPlaySurface, useContactMount, type ContactSurface } from './useContactMount';
import { useSignalSubtypeOptions, withSignalSubtypeOptions } from './useSignalSubtypeOptions';
import { useFlowContacts, useContactStatePillsWithSettle } from './hooks';
import { StatePills } from './StatePills';
import { useNotesSummaries } from '../shell-next/notes/useNotesSummaries';
import { useEnrollQueue, type ContactJumpHandler } from '../EnrollQueue';
import { rankByStateThenServerOrder } from './rankByState';
import { partitionDialableContacts } from '../trellus/dialableContacts';
// The static column config (renderers, header titles, mount filtering) moved
// to `./contactColumns` (thermo-nuclear review F1 Seam A) — a pure move.
import {
  CONTACT_COLUMNS,
  contactGroupValue,
  CONTACT_COLUMNS_UNNARROWED,
  CROSS_APP_STATE_LABEL,
  insertAfter,
  mountColumns,
  withoutPlayFieldConditions,
} from './contactColumns';

export interface ContactFlowTableProps {
  /** Account-scoped mount (drill-down / main-Terra tab): the account's full roster. */
  accountId?: string;
  /**
   * Which product SURFACE this table is mounted under (plan 2026-09-10-001
   * D9) — `'plays'` for the Plays tab, omitted (People) everywhere else. Not
   * a mount kind: it composes with `accountId`, and everything it changes
   * (working-state row, landing preset, role default, search box) is derived
   * in one place by `useContactMount`. The three play-SECTION surfaces
   * (`'play-responded'`, `'play-not-responded'`, `'play-candidates'` — plan
   * 2026-09-25-002 U6 / D6) are the Plays tab's "People in <Play>" sections —
   * pair each with `playScope` (`PLAY_SECTION_SURFACE` maps one to the other).
   */
  surface?: Exclude<ContactSurface, 'people'>;
  /**
   * The selected play and which of its sections this table lists (plan
   * 2026-09-25-002 U6; replaces `notInPlay`) — sent as `play_id` /
   * `play_type` / `play_section`, and shown as the section's locked filter
   * rows (`playSectionPinnedConditions`, D1). Pair with the matching
   * play-section `surface`.
   *
   * Part of the remount key: switching plays (or sections) starts from the
   * section's own working state rather than carrying the previous play's
   * live edits. That is also what keeps row-identity membership targets
   * honest (Codex 4-2): the table remounts per `(playId, section)` and the
   * query key carries `playScope`, so no row fetched for play A can ever
   * render — or be acted on — under play B's id.
   */
  playScope?: { playId: string; playType: string; section: PlayPeopleSection };
  /**
   * Imperative "switch to this preset/saved view" request from a sibling on
   * the SAME page (e.g. the account drill-down's "See Clay Users" link next
   * to the Workspaces section, 2026-08-26). A value present at MOUNT is instead the
   * mount's landing preset (the Signals row's Contacts card, 2026-10-21). NOT for initial-load deep-linking
   * — that's `?view=` in the URL, which `useViewStateMachine` only resolves
   * ONCE per mount (`resolvedRef`, useViewStateMachine.ts:480) and will NOT
   * react to on an already-mounted table. Every distinct value this prop
   * takes triggers exactly one `machine.openView(id)` call, tracked via a
   * ref (not state) so a StrictMode double-invoke of the effect body still
   * only opens the view once.
   */
  requestedViewId?: string;
  /** See FlowTableProps.fillHeight — forwarded as-is. */
  fillHeight?: boolean;
  /** Overrides the mount's `?view=` URL key — see `ContactMountInput.viewParam`. */
  viewParam?: string;
  /** Mount-constant. `false` for a roster nested in another table's expanded band — see `ContactMountInput.consumeContactParam`. */
  consumeContactParam?: boolean;
  /**
   * Called when the rep CHANGES the checked set with a checkbox (row, shift-range, header/group select-all,
   * "select all matching") — never for Clear or a post-commit prune. A page with several contact tables uses it to
   * make the table the page's one active dial owner (`tables/dialOwner.tsx`). Read through a ref at call time, so it
   * may change identity freely.
   */
  onUserSelect?: () => void;
}

/**
 * Headless People-table model (plan 2026-09-27-007 U2b, D1) — every hook
 * `ContactFlowTable` used to run inline between `useContactMount` and its
 * JSX, moved verbatim (same hooks, same order, including the render-time
 * "adjust state" blocks `prevContactQueryParam` / `prevRowsForPrune`), so a
 * V2 mount can drive the same mount facts, view machine, `?contact=`
 * deep-link, signal narrowing, pills, selection and jump-to-row plumbing
 * without forking it. `ContactFlowTable` is now a thin renderer over this
 * hook's `tableProps` / `bulk` / `banners`.
 *
 * Call it under the same remount boundary `ContactFlowTable` keys on
 * (`accountId|playId|section`): several pieces of state here (the view
 * machine's first-resolution guard, the selection, `filterBypass`) are
 * deliberately lifetime-scoped to one table identity.
 */
export function useContactTableModel({ accountId, surface, playScope, requestedViewId, fillHeight, viewParam, consumeContactParam, onUserSelect }: ContactFlowTableProps) {
  /**
   * Every mount fact in one derived object (D9) — which working-state row,
   * which landing preset, whether to restore/carry, which columns to hide,
   * who owns `?contact=`, what the role defaults get. Read `mount.<field>`
   * below; do NOT reintroduce a local mount-kind flag or a bare `accountId`
   * truthiness check — three parallel notions of "which mount is this" is
   * exactly how they used to drift apart.
   */
  const mount = useContactMount({ accountId, surface, viewParam, consumeContactParam });
  const identityQuery = useFlowIdentity();
  const identityReady = identityQuery.isSuccess || identityQuery.isError;
  const identity = identityQuery.data ?? null;

  // The Account column is NOT stripped from the view definition here (F7).
  // Hiding it in a drill-down is a RENDER concern — it's dropped from
  // `config.columns` below, which removes it from the table and from the
  // column picker while leaving `account_name` in the view's `columns`
  // array. Stripping it from the definition made the stripped list the dirty
  // baseline AND the Save payload, so opening a saved view in a drill-down
  // and pressing Save deleted the Account column from the stored view for
  // everyone.
  //
  // QA-2 (V3): `views` is now the SAME `CONTACT_PRESETS` array on both
  // mounts — no more `withAccountDefaultSort(CONTACT_PRESETS)` fork. That
  // helper folded `fit_score DESC` into the default preset's STORED
  // definition on an account mount, which meant "Save as new view" from a
  // drill-down baked the account-mount sort into the saved row for everyone
  // (spec forbids mount context in the model). The account mount's own
  // Fit-Score-first default is now a RENDER/QUERY-TIME override
  // (`effectiveSort`, below) applied on top of the real (global) sort,
  // never by mutating the view.
  const views = CONTACT_PRESETS;

  // Role default applied to a view as it becomes active (U6/D1). A preset
  // gets the rep's role-based rows MERGED alongside whatever the view
  // already filters on (`withRoleDefaults` — the one merge implementation,
  // shared with AccountFlowTable); a SAVED view owns its complete predicate
  // (doc: a view is the whole filter) and is returned untouched. Because the
  // adapted view is also the dirty BASELINE, a seeded role row never shows
  // up as an unsaved edit.
  //
  // Nothing structural happens here any more: the drill-down's Account-column
  // hide moved into `config.columns` (F7 — see the `views` note above), so
  // `adaptOnOpen` can never shrink a view's stored definition.
  //
  // Default contact filters are GLOBAL-MOUNT-ONLY (reversed 2026-08-27,
  // Annie Hale drill-down feedback — see roleDefaults.ts's module doc comment
  // for the full history). Until 2026-08-27 they seeded on BOTH mounts
  // (Spencer, 2026-07-31): the global CC table got the full ClayDR default
  // (ownership + meeting-booked exclusion); the account drill-down got ONLY
  // the meeting-booked exclusion. Reps reported those account-mount chips
  // reading as permanent, un-resettable carry-over on every drill-down, so an
  // account mount (`mount: 'account'` below) now seeds NOTHING for any role —
  // `withRoleDefaults` still runs unconditionally on both mounts, it just
  // no-ops on an account mount (`buildRoleDefaultFilter` returns `[]`, and
  // `withRoleDefaults` returns the same view reference — see its doc comment,
  // `:163` at time of writing).
  //
  // The synthetic "Terra Actions" column (CROSS_APP_STATE_KEY) is inserted
  // here UNCONDITIONALLY, for 'saved' views too, not just presets
  // (fixed 2026-08-12, see syntheticColumns.ts's doc comment for the full
  // regression). A saved view's STORED columns never carry it (the save
  // path strips it before every create/update), so without re-injecting
  // it on every open, a saved Contact view would silently lose the column
  // forever the first time it round-trips through Save. Because the insert
  // lands on the adapted view that becomes BOTH `working` and the dirty
  // `baseline`, it can never itself read as an unsaved edit. It is the ONLY
  // injected column. The play mount's SIGNAL_STATUS_KEY insert and the
  // "Clay Usage" USAGE_ROLE_KEY insert are both gone: `signal_status`
  // (D3/D10) and `usage_roles` ("Clay Role", 2026-09-25) are real catalog
  // fields, on the default column set / front-loaded by presets and addable
  // from the Columns picker on any view; injecting either would now fight
  // the rep's own column choices.
  //
  // The plays ROSTER surface's role-default seed is the L30 exclusion chip;
  // "Not in this play yet" seeds the Mine group instead (2026-09-25 PM
  // simplification) — both role-INDEPENDENT (roleDefaults.ts, D1/D2/D9) —
  // carried by `mount.roleDefaultOpts`.
  const adaptOnOpen = useCallback((view: FlowView, kind: ViewKind): FlowView => {
    const adapted = kind === 'saved' ? view : withRoleDefaults(view, identity, 'contact', mount.roleDefaultOpts);
    return { ...adapted, columns: insertAfter(adapted.columns, 'name', CROSS_APP_STATE_KEY) };
  }, [identity, mount]);

  // Server-side working-state store (U9 / R8; QA-2 / V3 gave the drill-down
  // its OWN row) — the rep's active view, unsaved modifications and default
  // landing view for `(user, stateKey)`. `stateKey` is DELIBERATELY not the
  // same thing as the `entity: 'contact'` passed to `useViewStateMachine`
  // below: `entity` means "which saved-views catalog" (GET /api/flow/views?
  // entity=, FOREIGN_PRESET_IDS) and never changes; `stateKey` means "which
  // working-state ROW" and forks by mount. The account-scoped roster
  // (drill-down / main-Terra Contact tab) gets `'contact_account'` — ONE row
  // for EVERY drill-down (a per-account row would multiply unboundedly), not
  // the Command Center table's `'contact'` row — so cross-join filters set in
  // a drill-down now survive a refresh/tab-switch instead of being silently
  // dropped (root cause: persistence used to be OFF entirely on this mount —
  // both the GET and the PUT were gated on "is this the global mount",
  // deleted below).
  //
  // The PLAYS SURFACE (D9) gets a THIRD row, `'contact_play'` — again ONE
  // row for EVERY play (not per play, which would multiply unboundedly), for
  // the same reason the drill-down shares one: the Plays tab's table must not
  // share the Contacts tab's `'contact'` row, or it would land on Tracker
  // carrying the global mount's role chips. It is also what restores the
  // surface's chips after a reload, replacing the `?play=&bucket=` URL state
  // plan 2026-09-09-003 used.
  const stateKey = mount.stateKey;
  const storedViewState = useFlowViewState(stateKey);
  // D5 (plan 2026-09-25-004, carried forward by the PM simplification): a
  // rep whose stored `contact_play_candidates` row is exactly the OLD
  // untouched default (preset + the L30 chip) is re-seeded with whatever
  // `adaptOnOpen` produces today (the Mine group) instead of restoring the
  // stale L30 seed forever — see playCandidatesLegacyDefault.ts's module doc.
  // A no-op reference on every other mount/surface and on any stored row
  // that isn't exactly that legacy shape.
  //
  // NOTE (plan 2026-10-06 item 1): that re-seed is now a no-op in practice.
  // Its only surface is 'play-candidates', which is a SECTION, and the wrapper
  // below replaces a section's restored working half outright — so there is no
  // longer a stored row left for the legacy detection to fire on. Kept rather
  // than deleted: it costs one reference comparison, and it is the correct
  // behaviour again the moment anyone re-enables the server restore for
  // sections. Deleting it (and its tests) is a clean standalone follow-up,
  // deliberately not bundled into this fix.
  //
  // Plan 2026-10-06 item 1 — the three play-SECTION mounts take their working
  // half from an in-session, client-only cache instead of their server row
  // (which is shared across every play and every session, so restoring it
  // replayed a months-old divergence as a permanent "Modified" cluster in
  // every play). Snapshotted once at mount: a remount is the only thing that
  // re-seeds, which is exactly a collapse/reopen or a `key={playId}` play
  // switch. Null on every other mount, and `withPlaySectionSessionWorkingState`
  // is then a by-reference no-op. See playSectionSessionViews.ts's module doc.
  const sessionSeed = usePlaySectionSessionSeed(mount);
  const viewState = useMemo(
    () => withPlaySectionSessionWorkingState(
      withoutLegacyPlayCandidatesWorkingState(storedViewState, mount.surface),
      mount,
      sessionSeed,
    ),
    [storedViewState, mount, sessionSeed],
  );

  // Saved views + presets + dirty tracking. Replaces the
  // old `switchView` local-state reset (the literal mechanism behind "every
  // time I toggle between Accounts and Contacts, filters reset").
  const machine = useViewStateMachine({
    entity: 'contact',
    // D3 (plan 2026-09-25-002) — the three play-section mounts share one
    // page, so each reads/writes its own `?view_<stateKey>=`; every other
    // mount keeps the page-global `?view=`.
    viewParam: mount.viewParam,
    // Old `?view=contact-product-users-l90` links open Clay Users silently.
    presetIdAliases: CONTACT_PRESET_ID_ALIASES,
    presets: views,
    // Landing preset (QA-2 / V6) — ONLY the global mount lands on Tracker by
    // default; the drill-down keeps landing on 'contact-all'
    // (CONTACT_DEFAULT_PRESET.id) regardless, since `shouldPartitionByState`
    // below and the account-mount sort override are both keyed off the
    // preset flagged `isDefault` ('contact-all'), not off whichever preset a
    // rep happens to land on. See CONTACT_LANDING_PRESET_ID's doc comment.
    // The plays surface lands on the 'contact-plays' preset (D10), whose
    // front-loaded Signal columns are what a rep reads while working a play
    // roster; Tracker's grouping is not what the Plays tab is for.
    // A `requestedViewId` present at MOUNT is the landing view (the Signals row's Contacts card lands on
    // 'Last Interesting Moment'); the URL param and a rep's saved Home still resolve before it. A later
    // change of the prop is the imperative `openView` effect below.
    defaultPresetId: requestedViewId ?? mount.landingPresetId,
    // Gates the FIRST resolution on identity AND on the restore GET having
    // settled, so the working state is seeded exactly once, from the right
    // source — no unfiltered-then-filtered flash and no restore landing
    // after the first fetch already went out on preset defaults. This is the
    // SAME gate the fetch below reads (`machine.isReady`), deliberately not
    // a second one stacked on top.
    ready: identityReady && viewState.isRestored,
    adaptOnOpen,
    viewState,
    // U2 / R1(b)(c) (2026-08-27, Annie Hale drill-down feedback) — an
    // ACCOUNT-scoped mount always lands on a view's own clean definition.
    // Both options are OFF for the global Command Center mount, which keeps
    // the full U9 restore and the 2026-08-12 view-switch filter carry.
    //
    // Why the account mount opts out: every drill-down shares ONE
    // `contact_account` row (`stateKey` above — deliberately kept, a
    // per-account row would multiply unboundedly), so restoring its working
    // half replayed account A's ad-hoc filters onto account B, and a preset
    // switch inside the drill-down carried them forward again. The row is
    // still WRITTEN (`useFlowViewStatePersist` below is unchanged) and its
    // `defaultViewId` pointer is still read — only the working-state restore
    // and the carry are off. Accepted tradeoff: a filter deliberately built
    // in a drill-down does not survive a remount.
    //
    // The play sections opt out on the same grounds one dimension over
    // (plan 2026-10-06 item 1: their row is shared across every play AND
    // every session). `sessionSeed` is what re-opens rung 2 for them — with
    // a payload THIS session produced, against a baseline the same
    // `adaptOnOpen` and the same identity computed, so a clean section
    // restores clean by construction and only a real edit reads "Modified".
    // Without a seed (the first mount of a page load) the restore stays off
    // and resolution falls through to `defaultViewId` and then the preset.
    skipWorkingRestore: !mount.restoreWorkingState && sessionSeed === null,
    disableFilterCarry: !mount.carryFilters,
  });
  const { filter: filters, groupLogic, sort, groupBy, columns: visibleColumns, columnWidths } = machine.working;
  const activeView = machine.activeView;

  // …and the write-back half: every dirty-relevant change, debounced ~1s.
  // QA-2 (V3): no longer gated on "is this the global mount" — the
  // account-scoped mount now owns its own row (`stateKey`, above) instead of
  // being locked out of persistence entirely.
  useFlowViewStatePersist({
    stateKey,
    enabled: machine.isReady,
    activeViewId: machine.activeViewId,
    working: machine.working,
  });

  // …and the session twin of that write, for the play sections only (plan
  // 2026-10-06 item 1). Same payload, no debounce: the server row only has to
  // be right eventually, whereas this cache has to be right at the instant a
  // play click unmounts the section that owns the state. A no-op on every
  // other mount.
  useRememberPlaySectionSessionView(mount, {
    enabled: machine.isReady,
    activeViewId: machine.activeViewId,
    working: machine.working,
  });

  // `requestedViewId` (2026-08-26, see prop doc comment) — an imperative
  // switch request from a sibling on the same page. Deliberately a ref, not
  // state: this only needs to DEDUPE repeated identical requests, not drive
  // a render, and a ref survives a StrictMode double-invoke of this effect's
  // body without opening the view twice (the second invocation sees the ref
  // already updated by the first and skips).
  const lastRequestedViewIdRef = useRef(requestedViewId);
  useEffect(() => {
    if (requestedViewId !== undefined && requestedViewId !== lastRequestedViewIdRef.current) {
      lastRequestedViewIdRef.current = requestedViewId;
      void machine.openView(requestedViewId);
    }
  }, [requestedViewId, machine]);

  const [search, setSearch] = useState('');
  // Input updates instantly; the server query only fires once typing pauses.
  // Search is deliberately NOT part of the view's working state — typing here
  // never dirties a view (doc's dirty-state table).
  const debouncedSearch = useDebouncedValue(search, 300);

  // `?contact=` — the SINGLE reader/writer of this param for every mount that
  // CONSUMES it (`mount.consumesContactParam`; all but a roster nested inside
  // another table's expanded band, which keeps its expansion local)
  // (global Command Center Contacts tab AND account-scoped drill-down /
  // main-Terra Contacts tab alike). Previously the account-scoped mounts got
  // their expand-target via an `initialExpandedId` prop threaded through
  // `AccountDrillDown`/`mini-apps/command-center/index.tsx`, while only the
  // GLOBAL mount read `?contact=` directly (see the Q4 comment further down
  // for that history) — the two mechanisms are now unified onto this one
  // param so the URL is always the shareable, persistent source of truth for
  // "which contact is expanded," on both mounts. `history: 'replace'` is
  // explicit here (nuqs's v2 default already is 'replace' — see the comment
  // by the jump effect below) so the contract is visible at the call site.
  const [contactQueryParam, setContactQueryParam] = useQueryState('contact', flowUrlParams.contact.withOptions({ history: 'replace' }));

  // Distinguishes a LOCAL write (the rep clicked a row — `FlowTable`'s
  // `onExpandedIdChange`) from an EXTERNALLY-ARRIVED value (a fresh mount
  // reading the URL, browser Back/Forward, or a link from elsewhere in the
  // app). Only the latter should re-trigger the scroll+flash jump animation
  // (global mount) or arm the filtered-out-contact bypass below — a rep's own
  // click already shows the row expanded and visible, nothing to jump to.
  const localWriteRef = useRef<string | null | undefined>(undefined);
  const handleExpandedIdChange = useCallback((id: string | null) => {
    localWriteRef.current = id;
    void setContactQueryParam(id);
  }, [setContactQueryParam]);

  // Deep-link target: `contactQueryParam` re-derived as "did this value just
  // arrive from outside this component," via the same render-time "adjusting
  // state when a prop changes" pattern as `prevGroupBy`/`collapsedOverrides`
  // in FlowTable.tsx (avoids an effect whose only job is one derived setState
  // call — `terra/no-derived-state-in-effect`). Replaces the old
  // `initialExpandedId` prop for: (a) seeding `filterBypass` below, and (b)
  // the scroll-into-view seam passed to FlowTable on account-scoped mounts.
  // A non-consuming (nested) mount observes `null` so it never arms the
  // banner, auto-expands, or re-targets the host table's param.
  const observedContactParam = mount.consumesContactParam ? contactQueryParam : null;
  const [prevContactQueryParam, setPrevContactQueryParam] = useState(observedContactParam);
  const [deepLinkTarget, setDeepLinkTarget] = useState<string | null>(
    observedContactParam && observedContactParam !== localWriteRef.current ? observedContactParam : null,
  );
  // GTMOPS-2254 — search deep-link into a filtered-out contact. A contact
  // opened via the omnibox (deep-linked `?contact=`) may not match the rep's
  // active filters, in which case the row would silently never render with
  // no error or explanation. The product decision (unconditional bypass): a
  // search-originated open ALWAYS pauses the active filters, no "is the
  // contact actually missing" check. RENDER/QUERY-TIME only — mirrors
  // `effectiveSort` below — never mutates `working`/the view definition
  // (mutating `working` would leak into `useFlowViewStatePersist`, below,
  // and wipe the rep's standing filters for everyone who reopens the view).
  // `!mount.isGlobal &&` preserves the pre-unification behavior: this bypass
  // has only ever armed for account-scoped mounts (the global mount's own
  // deep-link handling is scroll+flash, not a filter bypass — see the jump
  // effect below).
  //
  // Armed/cleared ONLY by a genuinely EXTERNAL `?contact=` arrival (the else
  // branch below — a deep link, Back/Forward navigation, or a cross-app
  // link), the "Restore filters" action (`onRestoreFilters` below), or a
  // manual filter edit (`handleFiltersChange` below). It is deliberately NOT
  // re-derived from `deepLinkTarget` (Codex adversarial-review fix,
  // 2026-09-11): `deepLinkTarget` also transitions to `null` on a purely
  // LOCAL row click/collapse (the `isLocalEcho` branch just below), and a
  // `filterBypass` derivation keyed off that transition used to silently
  // clear an active bypass on a local click — reapplying the rep's standing
  // filters and potentially hiding the very contact whose row was now
  // expanded, even though the URL still showed `?contact=<id>`.
  const [filterBypass, setFilterBypass] = useState(!mount.isGlobal && !!deepLinkTarget);
  if (observedContactParam !== prevContactQueryParam) {
    setPrevContactQueryParam(observedContactParam);
    const isLocalEcho = observedContactParam === localWriteRef.current;
    if (isLocalEcho) {
      setDeepLinkTarget(null);
    } else {
      setDeepLinkTarget(observedContactParam ?? null);
      localWriteRef.current = undefined;
      if (!mount.isGlobal) setFilterBypass(!!observedContactParam);
    }
  }

  // Restore-time sanitizer (round-2 Codex #1-client) — a persisted
  // `contact_play` row can still be carrying the retired A1 shape (a
  // `chip-play-scope` group naming `signal_instance_id`, or any group with a
  // condition on a `RETIRED_CONTACT_FILTER_FIELDS` key): the id predicate
  // silently no-ops server-side (`CONTACT_FIELD_MAP` has no entry for it) so
  // the group's OTHER conditions keep running wider than the rep can see —
  // "Legacy field" in the UI, but a live, invisible narrower/wider mismatch
  // on the wire. One-shot, the first time `machine.isReady` flips true:
  // `sanitizePlayScopeFilters` returns the SAME reference when the restored
  // row is already clean, so `hasSanitizedScopeRef` only ever gates a single
  // real `setFilters` call per mount, never a churn loop.
  //
  // This ref is only ever set to `true` inside this effect's SETUP body,
  // never in a cleanup, so it cannot get stuck `true` across StrictMode's
  // dev `setup → cleanup → setup` the way a cleanup-set lifetime flag would
  // (docs/v4-system/strictmode-resilient-effects.md) — the double-invoke
  // runs while `machine.isReady` is still `false` (restore hasn't settled
  // yet), so both setups return early and the ref is still `false` by the
  // time readiness actually flips on a later, real render.
  //
  // Deliberately a `setFilters` call (a POST-BASELINE change), not a
  // render-time bypass like `effectiveFilters` above: `useFlowViewStatePersist`
  // (below) debounces off `machine.working`, so this PUTs the sanitized row
  // back and the retired predicate never resurfaces on the next reload.
  const hasSanitizedScopeRef = useRef(false);
  useEffect(() => {
    if (!machine.isReady || hasSanitizedScopeRef.current) return;
    hasSanitizedScopeRef.current = true;
    const next = sanitizePlayScopeFilters(machine.working.filter);
    if (next !== machine.working.filter) machine.setFilters(next);
  }, [machine]);

  // U5: no scope state of any kind. The contacts universe is the whole mirror
  // for everyone, so the two mechanisms that used to widen it are gone:
  //  - `browseAll` ("Browse all contacts") escaped `scope=my` for an empty
  //    book. There is no `my` to escape now — the rep is already seeing
  //    everything, so the button would be a no-op.
  //  - the claydr `scope='all'` override existed because a ClayDR prospects
  //    across the whole mirror rather than the contacts they personally own.
  //    That's now the default for every role, and the ClayDR-specific
  //    narrowing is the VISIBLE `claydr_name = {me}` chip seeded below.

  // Account-mount "Fit Score first" default sort override (QA-2 / V3 —
  // RENDER/QUERY-TIME only, never mutates `working`/the view definition; see
  // CONTACT_ACCOUNT_DEFAULT_SORT's doc comment in @shared/flow/contact-view
  // for why the old `withAccountDefaultSort(CONTACT_PRESETS)` approach was a
  // save-side leak). Gate: (1) account-scoped, (2) still on the preset
  // flagged `isDefault` ('contact-all' — NOT necessarily the preset the rep
  // is currently landing on; see CONTACT_LANDING_PRESET_ID), (3) the CURRENT
  // sort still equals that preset's own (global) sort — a column-header
  // click or a switch to a different preset turns the override off, same
  // gate `shouldPartitionByState` below reuses for its own ranking decision.
  const isDefaultSortUnmodified = sort.field === machine.baseline.sort.field && sort.dir === machine.baseline.sort.dir;
  const isAccountDefaultView = mount.kind === 'account' && machine.isPreset && activeView.isDefault && isDefaultSortUnmodified;
  // ── U2 (plan 2026-09-25-001, findings 1-2 / 5-2) — play fields off the candidates mount ──
  // The `contact-play-candidates` preset sits in every contact switcher, but
  // its `play_*` sort/group/filter fields only resolve on a request carrying
  // `play_id` (a play-section mount's `playScope`) — the route 400s "Play columns need a selected play"
  // otherwise. Same render/query-time-only contract as the account override
  // above: `working`/the view definition is untouched (Save persists the
  // real sort), the QUERY and the header arrow see the degraded sort/group,
  // and the Filters popover keeps showing the rep's real chips. The server
  // 400 stays as the backstop.
  const playFieldsUnavailable = !playScope;
  const playSortUnavailable = playFieldsUnavailable && CONTACT_PLAY_FIELD_KEYS.includes(sort.field);
  const effectiveSort = playSortUnavailable
    ? CONTACT_PLAY_CANDIDATES_OFF_MOUNT_SORT
    : isAccountDefaultView ? CONTACT_ACCOUNT_DEFAULT_SORT : sort;
  const hiddenGroupBy = useMemo(() => (playFieldsUnavailable ? CONTACT_PLAY_FIELD_KEYS : []), [playFieldsUnavailable]);
  // Play-field levels only resolve on a play mount, so the Group menu does not show them elsewhere.
  const { shown: effectiveGroupBy, maxLevels: maxGroupLevels, restore: restoreGroupBy } = useMemo(
    () => splitHiddenGroupBy(groupBy, hiddenGroupBy),
    [groupBy, hiddenGroupBy],
  );

  // GTMOPS-2254 — same render/query-time-only contract as `effectiveSort`
  // above: the QUERY sees an empty filter set while bypassed, but `working`/
  // the view definition is untouched, and the Filters popover (below, via
  // `filters` — not `effectiveFilters`) keeps showing the rep's real standing
  // filters rather than lying that nothing is filtered.
  const effectiveFilters = useMemo(
    () => (filterBypass ? [] : playFieldsUnavailable ? withoutPlayFieldConditions(filters) : filters),
    [filterBypass, playFieldsUnavailable, filters],
  );
  // ── end U2 ──

  const query = useFlowContacts({
    accountId: mount.accountId,
    playScope,
    search: debouncedSearch,
    filters: effectiveFilters,
    groupLogic,
    sort: effectiveSort,
    groupBy: effectiveGroupBy,
    // B1 U2 (plan 2026-09-13-002) — the view's visible columns, read only to
    // decide whether to send `include=signals`.
    columns: visibleColumns,
    enabled: machine.isReady,
  });

  // D1 (plan 2026-09-25-002) — the section's defining predicate, SHOWN as
  // locked rows in the Filters popover. Display-only: the predicate itself is
  // the server-enforced `play_section` universe, never a FilterGroup, so
  // nothing about it enters working state, persists, or saves into a view.
  const playSection = playScope?.section;
  const pinnedConditions = useMemo(
    () => (playSection ? playSectionPinnedConditions(playSection) : undefined),
    [playSection],
  );

  const pages = useMemo(() => query.data?.pages ?? [], [query.data]);
  const rows = useMemo(
    () => pages.flatMap(p => p.rows),
    [pages],
  );

  // Empty-state ladder, most-specific cause first (R2: an empty table must
  // name what emptied it — the whole reason `scope` had to die).
  //
  //  1. a `{me}` filter is active → name the field (and, only when provable,
  //     the role mismatch) with a [Clear] that drops JUST that chip;
  //  2. the rep has diverged from the view's seeded baseline → offer to
  //     restore the baseline (the accounts table's existing pattern);
  //  3. account drill-down, still AT baseline, but the baseline itself
  //     carries a filter (a Saved view scoped by Account, the Tracker
  //     preset, a role-default row, …) → honest "no match for THIS VIEW",
  //     not a blanket claim about the account (QA-2 / V3: the old copy —
  //     "No contacts for this account." — asserted a fact the code can't
  //     know; a saved Account-scoped view landing on zero rows does NOT mean
  //     the account has no contacts at all);
  //  4. account drill-down with a genuinely empty baseline filter → the old
  //     copy is still honest here (nothing narrowed the roster);
  //  5. otherwise fall through to FlowTable's "No results."
  //
  // The old my-book CTA is gone with `scope` — see the note above.
  //
  // "Baseline" is the VIEW AS OPENED (`machine.baseline` — the preset or
  // saved view plus any injected role group), not the raw view filter and not
  // `length === 0`: every non-alumni contact preset ships the mandatory
  // alumni-exclusion group (contact-view.ts excludeAlumni), so a zero-filter
  // check could never fire on the default view, and a raw-view comparison
  // would read "touched" for a ClayDR who has done nothing but land on the
  // page.
  const baselineFilters = machine.baseline.filter;
  const filtersAreViewBaseline = JSON.stringify(filters) === JSON.stringify(baselineFilters);
  // A4 — the contact catalog's `signal_subtype` field offers the LIVE play
  // NAMES as options, so a rep can hand-rebuild any Plays-row preset from the
  // Filters popover by picking the play by name (the stored value IS the
  // name — A4 reversed A1's id-based scoping). Never blocks: while the plays
  // query is loading or failed this is the untouched catalog
  // (`withSignalSubtypeOptions` returns the same reference).
  // U5 (plan 2026-09-24-005, Item A) — a Signal-column sort/group-by the
  // server would 400 (`rejectUnsortableField`: not narrowed to one play) is
  // parked here instead of going on the wire, and the "Pick which play
  // first" prompt asks for the play. Plain state (no refs), so StrictMode's
  // double-invoke has nothing to get stuck on. Same narrowing rule as the
  // server (`requestNarrowsSignals`, shared in U4) over the filters the
  // QUERY actually sees (`effectiveFilters` — a bypassed deep link sends
  // none); an account-scoped mount is always narrow enough.
  const signalNarrowed = !!mount.accountId || requestNarrowsSignals(effectiveFilters, groupLogic);
  const [pendingSignalIntent, setPendingSignalIntent] = useState<
    { kind: 'sort'; sort: { field: string; dir: 'ASC' | 'DESC' } } | { kind: 'group'; levels: string[] } | null
  >(null);
  // U17 (Item C, D9) — warn, don't merge: Signal conditions spread over more
  // than one active group may each match a DIFFERENT signal row.
  const signalGroupCount = countSignalConditionGroups(effectiveFilters);
  // The prompt needs the live play names too, on every mount — not just the
  // Plays surface the Filters-popover picklist is gated to.
  const signalSubtypeOptions = useSignalSubtypeOptions({
    enabled: isPlaySurface(mount) || pendingSignalIntent !== null,
  });
  // U2 — the play fields are only offered where they resolve (the candidates
  // mount), the way `mountColumns` gates the play columns.
  const filterFields = useMemo(
    () => withSignalSubtypeOptions(
      mount.showPlayColumns ? CONTACT_FILTER_FIELDS : CONTACT_FILTER_FIELDS.filter(f => !CONTACT_PLAY_FIELD_KEYS.includes(f.key)),
      signalSubtypeOptions,
    ),
    [mount.showPlayColumns, signalSubtypeOptions],
  );

  const emptyState =
    buildMeFilterEmptyState({
      filters,
      filterFields,
      identity,
      onFiltersChange: machine.setFilters,
    })
    ?? (!filtersAreViewBaseline ? (
      <span>
        No contacts match the current filters.{' '}
        <Button variant="inline-text" size="xs" onClick={() => machine.setFilters(baselineFilters)}>
          Clear filters
        </Button>
      </span>
    ) : (mount.kind === 'account' ? (
      baselineFilters.length > 0 ? (
        <span>
          No contacts match this view for this account.{' '}
          <Button variant="inline-text" size="xs" onClick={() => machine.reset()}>
            Reset view
          </Button>
        </span>
      ) : 'No contacts for this account.'
    ) : baselineFilters.length > 0 ? (
      <span>
        No contacts match this view.{' '}
        <Button variant="inline-text" size="xs" onClick={() => machine.reset()}>
          Reset view
        </Button>
      </span>
    ) : undefined));

  // Batch pill fetch, ONE QUERY PER PAGE (Blake's PR #1088 fix — see the
  // doc comment on useContactStatePillsWithSettle in contact/hooks.ts for
  // why this replaced the old flat-accumulated-id-set version). Merges to
  // the same contactId → pills Map the column renderer below consumes;
  // `isSettled` additionally drives the account-mount partition below.
  const { pillsByContactId, isSettled: pillsSettled } = useContactStatePillsWithSettle(pages);

  // Notes summaries for the row bubbles — one query per keyset page, computed
  // here (beside the pills) because only the model has `pages`. The table wraps
  // the ledger in `NotesSummariesContext.Provider`. Empty while Notes is off.
  const notesPages = useMemo(
    () => pages.map(page => page.rows.map(row => ({ kind: 'contact' as const, id: row.id }))),
    [pages],
  );
  const notesSummaries = useNotesSummaries(notesPages);

  // Account-mount "Terra Actions first" default ranking (Spencer, 2026-07-28) —
  // same `isAccountDefaultView` gate as the sort override above, PLUS: the
  // pill batch for whatever's currently loaded has settled (reordering
  // per-chunk as pages stream in would visibly row-jump; before settle we
  // render the server order untouched, then apply exactly ONE reorder at
  // settle).
  const shouldPartitionByState = isAccountDefaultView && pillsSettled;
  const displayRows = useMemo(
    () => (shouldPartitionByState ? rankByStateThenServerOrder(rows, pillsByContactId) : rows),
    [rows, shouldPartitionByState, pillsByContactId],
  );

  // "View on contact →" jump wiring (flow-workflow-requests ITEM 4;
  // restructured in QA round 2, R4). RequestDetail.tsx's jump button calls
  // `jumpToContact`, which closes the sidebar detail and fires
  // `contactJumpRequest` (contactId + an incrementing nonce — see
  // ContactJumpRequest's doc comment in EnrollQueueContext.tsx for why a
  // bare contactId wouldn't re-fire on a second jump to the same contact).
  //
  // R4: this table no longer reacts to `contactJumpRequest` directly — that
  // left the signal with ZERO fallback whenever no contact table happened to
  // be mounted (the reported bug: "View on contact →" did nothing). Instead
  // this table REGISTERS a handler (`registerContactJumpHandler`) that
  // EnrollQueueContext's own always-mounted effect calls: attempt the
  // scroll+flash and report whether the row was found. The provider itself
  // falls back to a toast when no handler is registered or the handler
  // reports "not found" — see that file's header comment for the full
  // fallback contract.
  //
  // DOM-by-attribute rather than a ref map: FlowTable is a shared generic
  // component rendering an unknown number of rows: `data-row-id` (added to
  // FlowTable.tsx's <tr>) is the row-identity hook every external consumer
  // can target without FlowTable threading a new prop through its whole
  // render tree. Matched by attribute value (not a CSS-selector template
  // string) to sidestep CSS.escape, which isn't polyfilled in the test
  // environment and isn't universally available.
  //
  // Not a StrictMode lifetime ref (docs/v4-system/strictmode-resilient-effects.md):
  // there is no mount-scoped "cancelled"/"mounted" flag here — registration
  // is idempotent under StrictMode's dev double-invoke by construction (see
  // `registerContactJumpHandler`'s doc comment: the registry is a Set, so a
  // double `add` of this same handler is a no-op and a stale `delete` of an
  // already-removed handler is a no-op too — plan 2026-09-25-003 U2).
  // Extracted to a stable callback (Q4) so BOTH the live in-app jump signal
  // (registerContactJumpHandler below) AND the URL-param landing effect
  // further down reuse the exact same scroll+flash behavior — one
  // implementation, two entry points.
  const jumpToRow = useCallback((contactId: string): boolean => {
    const target = Array.from(document.querySelectorAll<HTMLElement>('[data-row-id]'))
      .find(el => el.getAttribute('data-row-id') === contactId);
    if (!target) return false;
    target.scrollIntoView({ block: 'center' });
    // Force a reflow between removing and re-adding the class so a SECOND
    // jump to the same contact re-triggers the CSS animation (matches the
    // approved prototype's `void el.offsetWidth` trick) instead of a no-op
    // (the class is already present, so a bare re-add wouldn't restart it).
    target.classList.remove('flow-row-flash');
    void target.offsetWidth;
    target.classList.add('flow-row-flash');
    return true;
  }, []);

  const { registerContactJumpHandler, groups: queueGroups, openCustomInvite } = useEnrollQueue();
  useEffect(() => {
    const handler: ContactJumpHandler = jumpToRow;
    return registerContactJumpHandler(handler);
  }, [registerContactJumpHandler, jumpToRow]);

  // R12 — single source of truth for "already queued" (any play), derived
  // ONCE per render from `useEnrollQueue().groups` (the `WorkflowsDropdown`
  // derivation, `ContactActionMenus.tsx:447-448`) and shared by BOTH the
  // row's queued indicator (`renderNameCellWithQueuedIndicator`, via
  // `config` below) AND `BulkEnrollPopover`'s per-play counts — nothing
  // else recomputes this.
  const queuedContactIds = useMemo(
    () => new Set(queueGroups.flatMap(g => g.items.map(i => i.contact_id))),
    [queueGroups],
  );

  // U6 (R1/R2) — the bulk-selection set. `ContactFlowTable` owns it
  // (`FlowTableConfig.selection` is controlled-component style — FlowTable
  // just resolves ids, the caller unions/toggles them into its own Set).
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  // Trellus: this model publishes NOTHING. The selection cluster's dial segment is the one publisher per page; it watches
  // `changeSeq`, which moves on every USER change (checkbox paths, Clear, Select-all-matching completion) and never on the
  // render-time prune below. The cluster partitions the dialable subset itself from `bulk.dialable`.
  const [changeSeq, setChangeSeq] = useState(0);
  const bump = useCallback(() => setChangeSeq(n => n + 1), []);
  const onUserSelectRef = useRef(onUserSelect);
  useEffect(() => {
    onUserSelectRef.current = onUserSelect;
  });
  // A checkbox change: take the page's dial ownership first (the owner store clears the previous owner), then bump.
  const markUserSelect = useCallback(() => {
    onUserSelectRef.current?.();
    bump();
  }, [bump]);
  const selection: FlowTableSelectionConfig<ContactRow> = useMemo(() => ({
    selectedIds,
    onToggle: id => {
      markUserSelect();
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
    },
    onToggleRange: ids => {
      markUserSelect();
      setSelectedIds(prev => new Set([...prev, ...ids]));
    },
    onToggleAllLoaded: ids => {
      markUserSelect();
      setSelectedIds(prev => {
        const allAlreadySelected = ids.length > 0 && ids.every(id => prev.has(id));
        if (allAlreadySelected) {
          const next = new Set(prev);
          for (const id of ids) next.delete(id);
          return next;
        }
        return new Set([...prev, ...ids]);
      });
    },
  }), [selectedIds, markUserSelect]);
  // Clear is a user action (it bumps `changeSeq`, so Trellus receives the empty set) and it is also what the page's owner
  // store calls to evict this table: a plain clear. It never claims ownership (`onUserSelect`). Bumping the generation
  // discards a "Select all matching" still in flight, so its completion cannot refill a table that was cleared or evicted.
  const selectAllGenerationRef = useRef(0);
  // A loop also dies when the table unmounts or the query's filters/source change under it: its `query.fetchNextPage`
  // would otherwise keep paging the NEW query (or a dead table). `mountedRef` resets at the top of the mount effect
  // (StrictMode runs setup→cleanup→setup); the scope effect bumps the generation on a real key change.
  const selectAllMountedRef = useRef(true);
  useEffect(() => {
    selectAllMountedRef.current = true;
    return () => {
      selectAllMountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    selectAllGenerationRef.current += 1;
  }, [mount.accountId, playScope, debouncedSearch, effectiveFilters, groupLogic, effectiveSort, effectiveGroupBy]);
  const clearSelection = useCallback(() => {
    selectAllGenerationRef.current += 1;
    bump();
    setSelectedIds(new Set());
  }, [bump]);
  const dialable = useMemo(() => partitionDialableContacts(rows, selectedIds), [rows, selectedIds]);
  const selectedIdsRef = useRef(selectedIds);
  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  });
  // Stable identity (a selection cluster's bundle depends on it): only refs and the stable `bump`/`setSelectedIds`.
  // Dropping enrolled ids shrinks what the rep sees checked, so it is a selection change Trellus must hear about:
  // bump only when at least one id really leaves (a no-op commit publishes nothing).
  const onCommitted = useCallback(
    (added: ReadonlySet<string>) => {
      if (![...selectedIdsRef.current].some(id => added.has(id))) return;
      bump();
      setSelectedIds(prev => new Set([...prev].filter(id => !added.has(id))));
    },
    [bump],
  );

  // U6 (R3) — select-all-matching: port `PlayhousePage.tsx:444-455`'s loop against THIS table's own `useFlowContacts`
  // (fetchNextPage/hasNextPage), then select every loaded row. The ids come straight from the last `fetchNextPage()`
  // result (`data.pages`, the same pages `rows` is flattened from), so completion does not depend on a render landing
  // between "busy" and "idle" — a fast cached page would batch the two away. It applies only if no Clear/eviction
  // happened since it started (`selectAllGenerationRef`), and it never claims page ownership (`onUserSelect`): the rep's
  // own action can finish in a table that is no longer the owner, and then it must neither reclaim nor clear the new
  // owner. It bumps `changeSeq` only.
  const [selectingAll, setSelectingAll] = useState(false);
  const selectAllMatching = useCallback(async () => {
    const startedAt = selectAllGenerationRef.current;
    setSelectingAll(true);
    try {
      let latest = query.data;
      let hasMore = true;
      const cancelled = () => !selectAllMountedRef.current || selectAllGenerationRef.current !== startedAt;
      while (hasMore) {
        if (cancelled()) break;
        const result = await query.fetchNextPage();
        if (cancelled()) break;
        latest = result.data ?? latest;
        // `fetchNextPage()` resolves with an error-state result rather than
        // rejecting (TanStack Query v5), and `hasNextPage` is derived from
        // the last SUCCESSFULLY cached page's `next_cursor` — so on a
        // persistently failing page, `hasNextPage` never flips false and a
        // bare `hasMore = result.hasNextPage ?? false` loops forever,
        // re-firing `fetchNextPage` in a sustained request storm. Stop and
        // surface the failure instead of trusting `hasNextPage` here.
        if (result.isError) {
          toast.error('Could not load all matching contacts — selection may be incomplete.');
          break;
        }
        hasMore = result.hasNextPage ?? false;
      }
      if (selectAllMountedRef.current && selectAllGenerationRef.current === startedAt) {
        bump();
        setSelectedIds(new Set((latest?.pages ?? []).flatMap(p => p.rows).map(r => r.id)));
      }
    } finally {
      if (selectAllMountedRef.current) setSelectingAll(false);
    }
  }, [query, bump]);

  // Email lookup for `BulkEnrollPopover`'s `requires_email` advisory gate —
  // built off the rows THIS table already has loaded (a selection can only
  // ever contain a loaded row's id; the popover never fetches contacts
  // itself).
  const emailByContactId = useMemo(
    () => new Map<string, string | null>(rows.map(r => [r.id, r.email])),
    [rows],
  );

  // U6 scope 2 (Decision 2, R2) — the ORDERED projection of `selectedIds`
  // that `BulkEnrollPopover` iterates and slices "first 100" from: current
  // rendered table order (`rows`), with any selected id no longer present
  // in the loaded rows (possible after a filter/search change narrows the
  // page) appended after. This is a flagged ASSUMPTION per the plan — table
  // order is the "what the rep is looking at" default; if Spencer wants
  // selection-click order instead at QA, only this projection changes.
  const orderedSelectedIds = useMemo(() => {
    const loadedIds = new Set(rows.map(r => r.id));
    const inTableOrder = rows.filter(r => selectedIds.has(r.id)).map(r => r.id);
    const leftover = [...selectedIds].filter(id => !loadedIds.has(id));
    return [...inTableOrder, ...leftover];
  }, [rows, selectedIds]);

  // Prune the bulk selection to rows the current query actually returned
  // (Reflection fix, 2026-09-01): a filter/view/search/sort change refetches
  // page 1 with a new queryKey, and ids no longer loaded must not ride along
  // — they'd read as "selected" in the toolbar while resolving to no-email in
  // the popover's counts (emailByContactId above only knows loaded rows).
  // Render-time "adjusting state when a prop changes" pattern (React docs;
  // same trick `filterBypass`/`prevInitialExpandedId` above use), NOT a
  // useEffect — a single derived `setSelectedIds` call is exactly what
  // `terra/no-derived-state-in-effect` flags, and computing it during render
  // is strictly earlier anyway (avoids the extra reconciliation pass an
  // effect-based prune would cost). Keyed on `rows` (reference-compared,
  // memoized off `pages` above) rather than a hand-built query fingerprint:
  // useFlowContacts uses placeholderData: keepPreviousData (hooks.ts:77), so
  // during a key transition `rows` is still the old page (selection ⊆ old
  // rows → no-op) and the prune lands exactly when the new page commits.
  // Growth (Load more, select-all page walk) is a structural no-op —
  // intersection never shrinks on a superset. `setSelectedIds` is skipped
  // entirely (not just a no-op update) when nothing needs pruning, so the
  // every-refetch identity churn of `rows` never causes an extra render.
  const [prevRowsForPrune, setPrevRowsForPrune] = useState(rows);
  if (rows !== prevRowsForPrune) {
    setPrevRowsForPrune(rows);
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const loaded = new Set(rows.map(r => r.id));
      const next = new Set([...prev].filter(id => loaded.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }

  // Q4 (QA round 3) — "View on contact" navigate-away fallback, landing
  // half: EnrollQueueContext.tsx navigates HERE with `?contact=<id>` when no
  // contact table was mounted anywhere to receive the jump directly (see
  // that file's header comment for the full fallback contract). Reused
  // scroll+flash via `jumpToRow` above, independent of
  // `contactJumpRequest`/`registerContactJumpHandler` — this is a fresh
  // mount reading the URL directly, not an in-app signal.
  //
  // `contactQueryParam`/`setContactQueryParam` are now declared once, above,
  // and shared by every mount (see that declaration's doc comment for the
  // history of the unification). This effect stays GLOBAL-MOUNT-ONLY
  // (`contactParam = accountId ? null : contactQueryParam`) because the
  // scroll+flash jump behavior only makes sense for the global roster —
  // account-scoped mounts get their expand target via `deepLinkTarget`/
  // FlowTable's `initialExpandedId` scroll seam instead (see above), not a
  // DOM query-and-flash.
  //
  // The URL is the shareable, persistent selection now (2026-09 — no longer a
  // transient jump that strips itself): landing here via a
  // `contactsInCommandCenter` link, or the rep clicking a row and later
  // reloading/sharing the URL, both keep `?contact=` in place so the same row
  // is expanded and scrolled to again. `jumpHandledRef` (not the presence of
  // the param) is what prevents the scroll+flash animation from replaying on
  // every unrelated re-render — each distinct incoming EXTERNAL param value
  // triggers it exactly once. A value that matches `localWriteRef.current`
  // (the rep's own click on this mount) is treated as already-handled: the
  // row is already visible/expanded, so re-scrolling and re-flashing it would
  // be a false "look over here" that never came from outside.
  //
  // Re-runs on every `rows` change (not just once on mount): the first page
  // of contacts may still be loading when this effect first fires, so a
  // single DOM query at mount time could false-negative on a row that just
  // hasn't rendered yet. Once the initial load settles (`!query.isLoading`)
  // and the row STILL isn't found, that's the "genuinely not found" case Q4
  // reserves the toast for — not an auto-paginating search, matching the
  // same no-further-fetch behavior the live jump signal already has (see
  // the "not in the current (possibly filtered) view" toast test).
  //
  // Read/written through nuqs, NOT react-router's `useSearchParams`: this
  // page's `?tab=` is a nuqs param whose (shallow) write react-router never
  // sees, so a react-router rewrite here rebases on a stale snapshot and
  // silently drops `tab=contact` — the same U1 transport collision documented
  // at the top of `views/useViewStateMachine.ts`. Every URL writer on this
  // page must share nuqs's one queue.
  const contactParam = mount.ownsContactParam ? contactQueryParam : null;
  const jumpHandledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!contactParam) {
      jumpHandledRef.current = null;
      return;
    }
    if (jumpHandledRef.current === contactParam) return;
    if (contactParam === localWriteRef.current) {
      // A local click on THIS mount already shows the row expanded/visible —
      // mark handled without scrolling/flashing.
      jumpHandledRef.current = contactParam;
      return;
    }
    const found = jumpToRow(contactParam);
    // Pre-seed counts as "still loading": the contacts query is DISABLED
    // until the role-default seeding settles (enabled: seeded), and a
    // disabled query reports isLoading === false — without the `!seeded`
    // arm this effect would conclude "genuinely not found" on the very
    // first render and toast before any row could load.
    if (!found && (!machine.isReady || query.isLoading)) return; // more of the first page may still be loading — try again next update
    jumpHandledRef.current = contactParam;
    if (!found) toast.error("Contact isn't in the current view");
  }, [contactParam, rows, machine.isReady, query.isLoading, jumpToRow]);

  // Account-scoped mounts already show the account on the page — drop the
  // detail's Account panel there (keyed useMemo mirrors AccountFlowTable).
  //
  // …and drop the ACCOUNT-LEVEL COLUMNS from the catalog for the same reason
  // (F7): in a drill-down every row belongs to the same account, so
  // `account_name` and the 12 account columns (U5) would repeat one value
  // down the page while the account's own header/panel already shows it.
  //
  // Doing it here rather than in the view definition is what makes the hide
  // display-only: FlowTable renders `visibleColumns` through this catalog
  // (`visibleColumns.map(k => colByKey[k]).filter(Boolean)`) and builds the
  // column picker from it, so they disappear from both while staying in the
  // view's `columns` array — and therefore in the dirty baseline and in every
  // Save / Save-as-new payload. ColumnsPopover keeps unknown keys in place
  // when it emits (it maps over the CURRENT ordered array), so a column edit
  // in a drill-down doesn't drop them either.
  // GTMOPS-2254 — a manual filter edit by the rep drops the bypass: once
  // they've touched filters themselves, silently continuing to ignore them
  // no longer makes sense.
  const handleFiltersChange = useCallback((groups: FilterGroup[]) => {
    setFilterBypass(false);
    machine.setFilters(groups);
  }, [machine]);

  // U5 — header-click / SortPicker sorts and Group-by picks route through
  // these so an unnarrowed Signal key parks as a pending intent instead of
  // reaching the wire. Non-Signal keys (and any key once narrowed) pass
  // straight through, unchanged.
  const handleSortChange = useCallback((next: { field: string; dir: 'ASC' | 'DESC' }) => {
    if (CONTACT_SIGNAL_FIELD_KEYS.includes(next.field) && !signalNarrowed) {
      setPendingSignalIntent({ kind: 'sort', sort: next });
      return;
    }
    machine.setSort(next);
  }, [machine, signalNarrowed]);

  const handleGroupByChange = useCallback((levels: string[]) => {
    const restored = restoreGroupBy(levels);
    if (levels.some(k => CONTACT_SIGNAL_FIELD_KEYS.includes(k)) && !signalNarrowed) {
      setPendingSignalIntent({ kind: 'group', levels: restored });
      return;
    }
    machine.setGroupBy(restored);
  }, [machine, signalNarrowed, restoreGroupBy]);

  // Apply: the narrowing chip REPLACES any previous one (never two), the
  // bypass drops (the rep's filters are meaningful again), then the parked
  // sort/group lands in the same batch — one request carries both.
  //
  // U14 (finding 5-1) — ONLY when the resulting filters really narrow. Under
  // OR with another group in play the flat filter model cannot express
  // `(G1 OR G2) AND play` (`applySignalScopeToFilters` returns null), so
  // the intent stays parked, the prompt explains why, and nothing goes on
  // the wire (the server would 400 it). The `requestNarrowsSignals` re-check
  // is the same guard the server runs — never trust, verify.
  const scopeBlockedReason = outerOrBlocksSignalScope(filters, groupLogic)
    ? "Your filters are combined with OR, so a play can't scope them. Switch the filters to AND (or clear them), then try again."
    : null;
  const applySignalScope = useCallback((name: string) => {
    if (!pendingSignalIntent) return;
    const next = applySignalScopeToFilters(machine.working.filter, machine.working.groupLogic, name);
    if (!next || !requestNarrowsSignals(next, machine.working.groupLogic)) return;
    setFilterBypass(false);
    machine.setFilters(next);
    if (pendingSignalIntent.kind === 'sort') machine.setSort(pendingSignalIntent.sort);
    else machine.setGroupBy(pendingSignalIntent.levels);
    setPendingSignalIntent(null);
  }, [machine, pendingSignalIntent]);

  const contactColumns = useMemo(
    () => mountColumns(signalNarrowed ? CONTACT_COLUMNS : CONTACT_COLUMNS_UNNARROWED, mount),
    [signalNarrowed, mount],
  );
  const config: FlowTableConfig<ContactRow> = useMemo(() => ({
    columns: [
      // D2 (flow-contacts-bulk-send design-fidelity plan) — the name column
      // no longer carries its own "Queued" badge (the retired
      // `renderNameCellWithQueuedIndicator`, which used to override this
      // column at construction time): a queued contact already signals
      // through the "Terra Actions" pill column below and the row-action
      // trigger's "✓ Queued" state, so a third badge here was redundant.
      // `queuedContactIds` (R12's single source of truth) is unchanged and
      // still consumed by `BulkEnrollPopover` and the D5 toolbar copy below.
      // Account / Signal / play column gates all live in `mountColumns`.
      ...contactColumns,
      {
        key: CROSS_APP_STATE_KEY,
        label: CROSS_APP_STATE_LABEL,
        type: 'text',
        noFilter: true,
        noSort: true,
        render: row => <StatePills pills={pillsByContactId.get(row.id)} />,
      },
    ],
    // Filterable fields are DECOUPLED from the visible columns (R5) — the
    // catalog carries `account_*` cross-join fields this table never shows.
    filterFields,
    rowKey: row => row.id,
    renderDetail: row => <ContactDetail row={row} />,
    // Floats at the row end (sticky right) — the prototype's + Enroll
    // affordance. flow-contacts-bulk-send U7 (decision 12): this mounts the
    // SAME `WorkflowsDropdown` component as the contact card and sidebar
    // search — the retired `EnrollButton` used to live here. Mounted
    // directly (not the full `ContactActionMenus` cluster) so the row shows
    // just the compact "Enroll ⌄" trigger, no "Actions ▾" alongside it.
    rowAction: row => <WorkflowsDropdown contactId={row.id} contactName={row.name} contactEmail={row.email} />,
    // Global CC People mount: the omnibox handles search; the account
    // drill-down and the plays surface keep the box (`mount.showSearch`).
    showSearch: mount.showSearch,
    // Tracker row chip (U7 / R6) — status lives on `row.tracker_status` etc.,
    // carried by the same /api/flow/contacts response (LEFT JOIN, no extra fetch).
    tracker: { entity: 'contact' },
    // F500 rank groups are headed "Parent rank: #N"; every other key reads its own cell.
    groupValue: contactGroupValue,
    // Generic checkbox multi-select (U3 / R1) — Contacts is the first real
    // consumer (U6); Accounts simply doesn't pass this.
    selection,
  }), [mount, contactColumns, filterFields, pillsByContactId, selection]);

  // D5 (flow-contacts-bulk-send design-fidelity plan, item 11) — how many of
  // the selection are already queued (any play), so the toolbar can warn
  // BEFORE the popover opens rather than only inside its per-play counts.
  // `queuedContactIds` is R12's single source of truth (above); this is a
  // second, independent derivation off the same set — the per-play counts
  // inside `BulkEnrollPopover` stay untouched.
  const selectedAlreadyQueued = useMemo(
    () => [...selectedIds].filter(id => queuedContactIds.has(id)).length,
    [selectedIds, queuedContactIds],
  );
  const addableCount = selectedIds.size - selectedAlreadyQueued;

  const tableProps: Omit<FlowTableProps<ContactRow>, 'toolbarTrailing'> = {
    views,
    viewMachine: machine,
    config,
    rows: displayRows,
    fillHeight,
    isLoading: !machine.isReady || query.isLoading,
    isRefetching: query.isPlaceholderData || (query.isFetching && !query.isFetchingNextPage && !query.isLoading),
    error: query.error ? query.error.message : null,
    onRetry: () => query.refetch(),
    hasMore: !!query.hasNextPage,
    onLoadMore: () => query.fetchNextPage(),
    isLoadingMore: query.isFetchingNextPage,
    activeViewId: machine.activeViewId,
    onViewChange: machine.openView,
    sort: effectiveSort,
    onSortChange: handleSortChange,
    filters,
    onFiltersChange: handleFiltersChange,
    filtersBypassed: filterBypass,
    onRestoreFilters: () => setFilterBypass(false),
    pinnedConditions,
    groupLogic,
    onGroupLogicChange: machine.setGroupLogic,
    search,
    onSearchChange: setSearch,
    groupBy: effectiveGroupBy,
    maxGroupLevels,
    onGroupByChange: handleGroupByChange,
    visibleColumns,
    onVisibleColumnsChange: machine.setColumns,
    // U10 — drag-resizable widths. Keyed by column key, so the synthetic
    // "Terra Actions" (CROSS_APP_STATE_KEY) column is resizable like any
    // other and its width simply persists alongside the rest; the
    // strip/reinsert this mount does on save never sees widths at all
    // (they aren't in `definitionOf`).
    columnWidths,
    onColumnWidthsChange: machine.setColumnWidths,
    totalLoaded: rows.length,
    identity,
    emptyState,
    // Non-consuming (nested) mounts fall to the renderers' uncontrolled branch.
    expandedId: mount.consumesContactParam ? contactQueryParam : undefined,
    onExpandedIdChange: mount.consumesContactParam ? handleExpandedIdChange : undefined,
    initialExpandedId: mount.isGlobal ? undefined : (deepLinkTarget ?? undefined),
  };

  return {
    mount,
    machine,
    identity,
    tableProps,
    notesSummaries,
    bulk: {
      selectedIds,
      addableCount,
      selectedAlreadyQueued,
      clear: clearSelection,
      changeSeq,
      dialable,
      selectAllMatching,
      selectingAll,
      hasNextPage: query.hasNextPage,
      orderedSelectedIds,
      queuedContactIds,
      emailByContactId,
      // U6 scope 2 (Decision 15): server-authoritative pruning — only
      // ids the response reported `added` leave the selection.
      // Skipped, cap-truncated, and client-excluded ids stay checked
      // so the rep can immediately commit the next chunk.
      onCommitted,
      // Gate for the Custom Invite toolbar button (see its JSX comment in
      // ContactFlowTable.tsx).
      showCustomInvite: isPlaySurface(mount),
      openCustomInvite,
    },
    banners: {
      signalGroupCount,
      pendingSignalIntent,
      signalSubtypeOptions,
      scopeBlockedReason,
      applySignalScope,
      dismissSignalIntent: () => setPendingSignalIntent(null),
    },
  };
}

export type UseContactTableModelResult = ReturnType<typeof useContactTableModel>;
